import { Component, type ErrorInfo, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { ErrorScreen } from '@/components/ErrorScreen'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  /** When this changes (e.g. pathname), a prior error state is cleared. */
  resetKey?: string
}

interface State {
  hasError: boolean
  resetKey?: string
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, resetKey: props.resetKey }
  }

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true }
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    // Only clear when recovering from an error — avoid a state write on every route change.
    if (state.hasError && props.resetKey !== state.resetKey) {
      return { hasError: false, resetKey: props.resetKey }
    }
    return null
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info)
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <ErrorScreen
            variant="server"
            onRetry={() => this.setState({ hasError: false })}
            showHomeLink
          />
        )
      )
    }
    return this.props.children
  }
}

/** Resets the error UI on client-side navigations so a stuck page cannot lock the app. */
export function RoutedErrorBoundary({ children, fallback }: Omit<Props, 'resetKey'>) {
  const location = useLocation()
  return (
    <ErrorBoundary resetKey={location.pathname} fallback={fallback}>
      {children}
    </ErrorBoundary>
  )
}
